import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Button, Spin, Typography } from "@murm/ant";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { PENDO_ID, pendoId } from "../../lib/pendoIds";
import styles from "./CommandPalette.module.css";

const { Text, Link } = Typography;

// A ref callback, not an effect: React calls it when a row becomes the highlighted one. `nearest`
// scrolls only if the row is clipped by the palette's scroll area.
const scrollIntoViewNearest = (node) => node?.scrollIntoView?.({ block: "nearest" });

/**
 * The dropdown that renders under the search bar while typing (or while a term is being
 * configured). Presentational + its own highlight-navigation state, but does NOT own the
 * keystrokes themselves -- those land on the actual text input, which forwards them here via
 * the exposed `handleKeyDown` ref method. This is the standard combobox split: the input owns
 * focus/keydown, the listbox just reflects external state.
 *
 * Content modes when searching (`configPanel` absent), by character count:
 * - 0 chars, existing user, nothing committed yet: a hint row + RECENT SELECTIONS (last 5 picks).
 * - 0 chars, existing committed content: Run Preview (Zone 1) previews the whole query and is
 *   default-highlighted; `relatedMatches` -- a just-closed token's siblings, not typed matches --
 *   keep showing as "ADD TO YOUR SEARCH" instead of "RELATED MATCHES".
 * - 1-2 chars (`mode: "prefix"`): Zone 1 (Run Preview) always shown and default-highlighted,
 *   even alongside real matches.
 * - 3+ chars (`mode: "exact"`): Zone 1 only when there are zero matches at all (falls back with
 *   a "no matches" message + catalog link in that case).
 * The primary bucket's own header is always "STARTS WITH ..." at every length, independent of
 * `mode` -- `matchTerms.js`'s `exactMatches` has done word-boundary prefix matching at every
 * character count for a while now, so "EXACT MATCHES" would be inaccurate past 2 characters too.
 *
 * Configuring (`configPanel` present or `selectedTerm` set): "SELECTED" header + the term being
 * configured + the config panel itself (not this component's job) + a flat
 * "ADD TO YOUR SEARCH" list reusing the same exact/related matches, no exact/related split in
 * that mode (per the mockup).
 */
const CommandPalette = forwardRef(function CommandPalette(
  {
    fragment = "",
    // What "Run your search" actually previews (below) -- distinct from `fragment`, which stays
    // the single last word for matching/highlight-reset purposes. Defaults to `fragment` so a
    // caller that hasn't been updated to pass the full typed text still gets the old behavior.
    literalText = fragment,
    headerText = null,
    mode = null,
    exactMatches = [],
    relatedMatches = [],
    recentSelections = [],
    placeStatus = "idle",
    onRetryPlaces,
    onCommitTerm,
    onRunLiteral,
    onOpenCatalog,
    configPanel = null,
    selectedTerm = null,
    onRemoveSelected,
  },
  ref
) {
  // Driven by selectedTerm, not configPanel: a term can be "being configured" (show the
  // SELECTED row + remove button + flat ADD TO YOUR SEARCH list) without having a type-specific
  // config panel to show at all -- e.g. an unresolved template token. configPanel being absent
  // must not make this look like plain searching mode.
  const isConfiguring = Boolean(selectedTerm);
  const trimmedFragment = fragment.trim();
  const hasMatches = exactMatches.length > 0 || relatedMatches.length > 0;
  const showRecentSelections =
    !isConfiguring && trimmedFragment.length === 0 && recentSelections.length > 0;
  // Zone 1 always shows in prefix mode (even with real matches below it) -- everywhere else,
  // only as a fallback when there are zero matches. Never shows for an empty fragment (nothing to
  // "run"), except while configuring (previews the FULL query via `literalText`, not the
  // single-word `fragment`, so Enter can run it without touching the config panel first) or once
  // there's a whole committed query to preview even with the config panel closed (`literalText`
  // is empty for a genuinely fresh bar, so this can't fire alongside RECENT SELECTIONS).
  const showRunPreview =
    isConfiguring ||
    (trimmedFragment.length > 0 && (mode === "prefix" || !hasMatches)) ||
    (trimmedFragment.length === 0 && literalText.trim().length > 0);
  const showNoMatchesMessage = !isConfiguring && mode === "exact" && !hasMatches;
  const rows = isConfiguring
    ? []
    : showRecentSelections
      ? recentSelections
      : [...exactMatches, ...relatedMatches];
  // Prefix mode's Zone 1 is a navigable "-1" position (not just a fallback row): default-
  // highlighted, arrow-down moves into the real rows below, arrow-up moves back to it. Same for
  // the empty-fragment Run Preview above -- Enter runs the search, not the first leftover row.
  const usesZone1AsDefaultHighlight =
    (mode === "prefix" && trimmedFragment.length > 0) ||
    (trimmedFragment.length === 0 && showRunPreview);

  const [highlightedIndex, setHighlightedIndex] = useState(usesZone1AsDefaultHighlight ? -1 : 0);
  // The Run row is lit only while Enter would run it: it's the -1 position, nothing else is
  // navigable, or a token is being configured (Enter runs the whole query there).
  const runPreviewHighlighted = isConfiguring || highlightedIndex === -1 || rows.length === 0;

  // Resetting the highlight when `fragment`/`mode`/`isConfiguring` change is adjusted during
  // render (React's documented pattern for "reset state when a prop changes") rather than in a
  // useEffect, which would cost an extra commit -- keyed on the primitives, not the
  // exactMatches/relatedMatches arrays themselves, since a caller that doesn't memoize those
  // (e.g. a fresh [] every render when a prop is simply omitted) would otherwise reset the
  // highlight on every keystroke-triggered re-render, as array identity, not content, would drive it.
  const resetKey = `${fragment}|${mode}|${isConfiguring}`;
  const previousResetKeyRef = useRef(resetKey);
  if (previousResetKeyRef.current !== resetKey) {
    previousResetKeyRef.current = resetKey;
    setHighlightedIndex(usesZone1AsDefaultHighlight ? -1 : 0);
  }

  useImperativeHandle(
    ref,
    () => ({
      handleKeyDown(event) {
        if (isConfiguring) {
          // Real bug: this used to preventDefault + remove the whole token on EVERY Backspace
          // while configuring, even with real typed text after it (e.g. "...of 41-60 among
          // wome|" -> fixing "wome" to "women") -- the browser's own normal one-character
          // deletion never got a chance to run. Only remove the token when there's nothing left
          // to backspace locally, mirroring the non-configuring
          // "Backspace on empty removes the previous token" rule elsewhere in this file.
          if (event.key === "Backspace" && trimmedFragment.length === 0) {
            event.preventDefault();
            onRemoveSelected?.();
          } else if (event.key === "Enter") {
            // Real gap ("missing run your search part"): pressing Enter while a token is still
            // configuring (e.g. right after committing "Age", defaults showing) did nothing at
            // all -- the footer's own "enter Select or run" hint promised this. Runs the whole
            // query as-is, same as the Zone 1 row below does when it's visible.
            event.preventDefault();
            onRunLiteral?.();
          }
          return;
        }

        const floor = usesZone1AsDefaultHighlight ? -1 : 0;
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setHighlightedIndex((i) => Math.min(i + 1, Math.max(rows.length - 1, floor)));
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          setHighlightedIndex((i) => Math.max(i - 1, floor));
        } else if (event.key === "Enter") {
          event.preventDefault();
          // `rows.length > 0` alone doesn't prove `highlightedIndex` is still IN bounds -- rows
          // aren't only grown by typing (which resets the highlight via `resetKey` above): late
          // place matches (usePlaceMatches) can replace a bigger stale result set with a smaller
          // fresh one for the same fragment, shrinking `rows` without any reset. Real bug: that
          // left a stale `highlightedIndex` pointing past the end of the new, shorter `rows`,
          // and `onCommitTerm?.(undefined)` crashed (callers read `term.display` unconditionally).
          const row = highlightedIndex >= 0 ? rows[highlightedIndex] : undefined;
          if (row) {
            onCommitTerm?.(row);
          } else {
            onRunLiteral?.();
          }
        }
      },
    }),
    [
      isConfiguring,
      rows,
      highlightedIndex,
      usesZone1AsDefaultHighlight,
      onCommitTerm,
      onRunLiteral,
      onRemoveSelected,
    ]
  );

  // Search Guidance "First 1-2 Characters" / "Exact Match": the typed prefix is bold, the rest regular.
  const renderTermName = (display) => {
    const prefixLength =
      trimmedFragment && display.toLowerCase().startsWith(trimmedFragment.toLowerCase())
        ? trimmedFragment.length
        : 0;
    if (!prefixLength) return display;
    return (
      <>
        <strong>{display.slice(0, prefixLength)}</strong>
        {display.slice(prefixLength)}
      </>
    );
  };

  const renderRow = (term, index, isHighlighted) => (
    <div
      key={term.id}
      ref={isHighlighted ? scrollIntoViewNearest : undefined}
      {...pendoId(PENDO_ID.PALETTE_TERM_ROW, term.sourceType)}
      role="option"
      aria-selected={isHighlighted}
      onClick={() => onCommitTerm?.(term)}
      className={isHighlighted ? `${styles.row} ${styles.highlighted}` : styles.row}
    >
      <div>
        <div className={styles.termName}>{renderTermName(term.display)}</div>
        <Text type="secondary" className={styles.meta}>
          {term.category}
        </Text>
      </div>
      {isHighlighted && <kbd className={styles.key}>enter</kbd>}
    </div>
  );

  // Figma's "Command Palette Anatomy" spec: max height 440, the keyboard-hint footer stays
  // pinned at the bottom (CommandPalette.module.css) and only the content above it scrolls.
  return (
    <div className={styles.panel}>
      <div className={styles.scroll}>
        {showRunPreview && (
          <div
            {...pendoId(PENDO_ID.PALETTE_RUN_LITERAL)}
            role="option"
            aria-selected="true"
            onClick={() => onRunLiteral?.()}
            className={
              runPreviewHighlighted
                ? `${styles.row} ${styles.runRow} ${styles.highlighted}`
                : `${styles.row} ${styles.runRow}`
            }
          >
            <span className={styles.runLabel}>
              <SearchOutlined className={styles.runIcon} />
              <span data-run-preview>
                Run your search: &quot;<strong>{literalText}</strong>&quot;
              </span>
            </span>
            {runPreviewHighlighted && <kbd className={styles.key}>enter</kbd>}
          </div>
        )}

        {/* Place-lookup status -- only the async half of matching can be slow/fail;
            term/filter matching is synchronous. Previous matches (rendered below, unchanged)
            stay up rather than being blanked out while this shows. */}
        {!isConfiguring &&
          !showRecentSelections &&
          (placeStatus === "searching" || placeStatus === "stillSearching") && (
            <div className={styles.status}>
              <Spin size="small" />
              <Text type="secondary">
                {placeStatus === "searching" ? "Searching..." : "Still searching..."}
              </Text>
            </div>
          )}
        {!isConfiguring && !showRecentSelections && placeStatus === "error" && (
          <div className={styles.status}>
            <ExclamationCircleOutlined />
            <Text type="secondary">Couldn&apos;t load matches</Text>
            <Link {...pendoId(PENDO_ID.PALETTE_RETRY_PLACES)} onClick={() => onRetryPlaces?.()}>
              Try again
            </Link>
          </div>
        )}

        {isConfiguring ? (
          <>
            <Text type="secondary" className={styles.sectionLabel}>
              ● SELECTED
            </Text>
            <div className={`${styles.row} ${styles.highlighted} ${styles.selected}`}>
              <div>
                <div className={styles.termName}>{selectedTerm?.display}</div>
                <Text type="secondary" className={styles.meta}>
                  {selectedTerm?.category}
                </Text>
              </div>
              <Button
                {...pendoId(PENDO_ID.PALETTE_REMOVE_SELECTED)}
                type="link"
                size="small"
                onClick={() => onRemoveSelected?.()}
              >
                remove ×
              </Button>
            </div>

            {configPanel}

            {hasMatches && (
              <>
                <Text type="secondary" className={styles.sectionLabel}>
                  ADD TO YOUR SEARCH
                </Text>
                {[...exactMatches, ...relatedMatches].map((term) => renderRow(term, -1, false))}
              </>
            )}
          </>
        ) : showRecentSelections ? (
          <>
            <Text italic className={styles.hint}>
              Start typing to see matching data or start from a recent selection.
            </Text>
            <Text type="secondary" className={styles.sectionLabel}>
              RECENT SELECTIONS
            </Text>
            {recentSelections.map((term, i) => renderRow(term, i, i === highlightedIndex))}
          </>
        ) : (
          // Dimmed (not hidden) while a place lookup is in flight -- these are the SAME matches
          // as before the lookup started (they're never cleared mid-fetch, see
          // usePlaceMatches.js), just visually deprioritized under the status row above.
          <div
            className={
              placeStatus === "searching" || placeStatus === "stillSearching"
                ? styles.dimmed
                : undefined
            }
          >
            {exactMatches.length > 0 && (
              <>
                <Text type="secondary" className={styles.sectionLabel}>
                  {/* Always "STARTS WITH", independent of `mode` -- see this file's own docstring. */}
                  {`STARTS WITH "${(headerText || trimmedFragment).toUpperCase()}"`}
                </Text>
                {exactMatches.map((term, i) => renderRow(term, i, i === highlightedIndex))}
              </>
            )}
            {relatedMatches.length > 0 && (
              <>
                <Text type="secondary" className={styles.sectionLabel}>
                  {/* Empty fragment: these are a just-closed token's siblings, not typed matches. */}
                  {trimmedFragment.length === 0 ? "ADD TO YOUR SEARCH" : "RELATED MATCHES"}
                </Text>
                {relatedMatches.map((term, i) => {
                  const index = exactMatches.length + i;
                  return renderRow(term, index, index === highlightedIndex);
                })}
              </>
            )}
            {showNoMatchesMessage && (
              <div className={styles.noMatches}>
                <Text type="secondary">No matches found. </Text>
                <Link {...pendoId(PENDO_ID.PALETTE_CATALOG_LINK)} onClick={() => onOpenCatalog?.()}>
                  Browse the data catalog
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Keyboard hints as keycaps (Search Guidance palette footer); esc Close sits on the right. */}
      <div className={styles.footer}>
        <span className={styles.hintItem}>
          <kbd className={styles.key} aria-label="Up arrow">
            <ArrowUpOutlined />
          </kbd>
          <kbd className={styles.key} aria-label="Down arrow">
            <ArrowDownOutlined />
          </kbd>
          Navigate
        </span>
        <span className={styles.hintItem}>
          <kbd className={styles.key}>enter</kbd>
          Select or run
        </span>
        <span className={styles.hintItem}>
          <kbd className={styles.key} aria-label="Backspace">
            ⌫
          </kbd>
          Remove
        </span>
        <span className={`${styles.hintItem} ${styles.hintEnd}`}>
          <kbd className={styles.key}>esc</kbd>
          Close
        </span>
      </div>
    </div>
  );
});

export default CommandPalette;
